/**
 * audio-track-sidecar.js — window.AudioTrackSidecar
 *
 * Play a DIFFERENT audio track of a file than the one the <video> element
 * decodes, without rebuilding the file.
 *
 * WHY THIS EXISTS. Choosing which language you hear used to go through
 * UniversalVideoDecode.open(file, {audioIndex, forceRemux:true}) — a whole-file
 * remux. That copies every byte of video the operation does not touch and
 * re-encodes the audio, so the cost of a track switch was O(size of the file)
 * when the information that actually changes is O(size of one audio track).
 *
 * Measured on an 820 MB / 24-minute HEVC-10bit + FLAC MKV (the shape of the
 * file in report #1514), through this build's FFmpegProcessor, real Chrome:
 *
 *     whole-file remux (what the tool did)       45.6 s     767 MB out
 *     whole audio track, re-encoded to AAC       51.2 s      33 MB out
 *     whole audio track, -c:a copy               17.1 s      20 MB out
 *     ONE 15 s segment, AAC, at 0 / 700 / 1380 s  1.9-2.3 s  0.35 MB out
 *     ONE 15 s segment, -c:a copy (lossless)      0.25 s     0.33 MB out
 *
 * The segment cost does not depend on WHERE in the file it is taken, so the
 * time to first sound is constant no matter how big the file is — which is the
 * property that keeps this fixed. The user in #1514 was 70 s into a bar at 42%
 * when they gave up and filed "the player goes into infinite loading".
 *
 * HOW. Extract the chosen track segment by segment into fragmented MP4, append
 * to a MediaSource driving a hidden <audio>, and keep that element in lock-step
 * with the <video> through VideoAudioSync. The <video> keeps whatever source
 * the decode ladder gave it — native HW decode included — and is silenced
 * through WebAudio (AudioEnhance.setSilent), NOT through .muted: .muted and
 * .volume are the viewer's own controls and the player writes them directly.
 *
 * Segments are appended in 'segments' mode with an explicit timestampOffset, so
 * the audio timeline is ABSOLUTE: a segment may be produced in any order, a
 * seek costs one segment, and seeking back into an already-played stretch costs
 * nothing.
 *
 * LANE. -c:a copy when this browser's MediaSource really takes the source codec
 * in fMP4, else AAC. The choice is verified EMPIRICALLY on the first segment
 * (addSourceBuffer + a real append), because isTypeSupported answers about the
 * build, not about the decoder — see codec-matrix.js for the same lesson.
 *
 *   var side = AudioTrackSidecar.attach(file, {
 *       video: playerVideoEl, relIndex: 1, codec: 'flac', duration: 1440,
 *       onFirstSound(i){}, onProgress(i){}, onError(e){}
 *   });
 *   side.element(); side.readySeconds(); side.lane(); side.stop();
 *
 * attach() MUST be called synchronously from the click that asked for the
 * track: it arms the <audio> through __audioGate while the user activation is
 * still alive (the first segment lands seconds later, long after Safari and
 * Chrome-on-Android have dropped it).
 */
(function () {
'use strict';
if (typeof window === 'undefined') return;

/* Source codec -> the fMP4 codec string we would have to promise MediaSource
   if we copied the track through untouched. Only codecs that are legal in
   ISOBMFF and that some browser really decodes are listed; everything else
   (ac3/eac3/dts/truehd/mp3/pcm/vorbis/wmav2…) goes down the AAC lane, which is
   the same decision codec-mux-rules.js makes for the muxer. */
var COPY_MIME = {
    flac: 'audio/mp4; codecs="flac"',
    aac:  'audio/mp4; codecs="mp4a.40.2"',
    opus: 'audio/mp4; codecs="opus"'
};
var AAC_MIME = 'audio/mp4; codecs="mp4a.40.2"';
/* +frag_keyframe+empty_moov+default_base_moof is what MSE can take; the same
   flags progressive-decode.js uses for video. */
var FRAG = ['-movflags', '+frag_keyframe+empty_moov+default_base_moof'];
/* FLAC in MP4 is ISO-legal but this ffmpeg build still calls the muxer
   experimental, and refuses without -strict -2 ("Could not write header for
   output file #0 … Experimental feature", measured). */
var COPY_ARGS = ['-c:a', 'copy', '-strict', '-2'];
/* Pinned, exactly as in progressive-decode.js: a SourceBuffer that gets a
   different sample rate or channel count mid-stream rejects the append. */
var AAC_ARGS = ['-c:a', 'aac', '-b:a', '192k', '-ac', '2', '-ar', '48000'];
/* Below this a produced segment carries no real audio — same signal
   progressive-decode.js uses to tell "past the end" from "produced nothing". */
var EMPTY_SEGMENT_BYTES = 2 * 1024;

/* iPhone Safari has no plain MediaSource — it has ManagedMediaSource (17.1+),
   which is the same API with the engine allowed to evict more freely. Taking
   whichever exists is what makes this module reach iOS at all; without it every
   iPhone falls back to the whole-file remux this module exists to remove.
   ⚠️ NOT verified on real iOS hardware — there is none here, and saying
   otherwise would be a guess dressed as a measurement. What IS verified is that
   the ManagedMediaSource code path works when it is present (exercised through
   a shim) and that a browser with NEITHER falls back cleanly. And the proof of
   audibility below means a variant that accepts bytes without decoding costs
   seconds, not a silent film. */
function _mseCtor() { return window.ManagedMediaSource || window.MediaSource || null; }

/* Media deadlines count VISIBLE time (timbrica-core.js __visibleTimeout):
   Chromium does not load a media element in a hidden tab that has not played
   media yet, so a wall-clock deadline reads «the viewer is in another tab» as
   «this lane is dead» (the /video-player sourceopen_timeout class, 23.09.2026). */
function _visibleDeadline(fn, ms) {
    if (typeof window.__visibleTimeout === 'function') return window.__visibleTimeout(fn, ms);
    var h = setTimeout(fn, ms);
    return function () { clearTimeout(h); };
}
function _visibleNow() {
    return (typeof window.__visibleNow === 'function') ? window.__visibleNow() : (window.performance || Date).now();
}

function isSupported() {
    try {
        var C = _mseCtor();
        return !!(C && C.isTypeSupported && C.isTypeSupported(AAC_MIME) &&
                  window.FFmpegProcessor && window.VideoAudioSync);
    } catch (_) { return false; }
}

function ae(f, p) {
    try { if (window.__ae) window.__ae('feature_use', Object.assign({ f: f }, p || {})); } catch (_) {}
}

/* Silence the element WITHOUT touching .muted/.volume — those belong to the
   viewer, and the player writes them directly (video-player.js binds the mute
   button to `video.muted = !video.muted`). Routing the element through
   WebAudio at zero gain is the only mute that the volume UI cannot undo, and
   AudioEnhance already owns that capture for this page, so asking it keeps ONE
   createMediaElementSource per element — a second one throws. */
function silenceVideo(video) {
    if (!video || !window.AudioEnhance || !window.AudioEnhance.attach) return null;
    var fx = null;
    try { fx = window.AudioEnhance.attach(video); } catch (_) { return null; }
    if (!fx || typeof fx.setSilent !== 'function') return null;
    var ok = fx.setSilent(true);
    if (!ok) return null;
    try { fx.resume().catch(function () {}); } catch (_) {}
    return {
        release: function () { try { fx.setSilent(false); } catch (_) {} }
    };
}

function attach(file, opts) {
    opts = opts || {};
    var video = opts.video;
    var relIndex = opts.relIndex | 0;
    var duration = +opts.duration || 0;
    var segLen = Math.max(4, +opts.segment || 15);
    /* Audio is small — 0.33 MB per 15 s copied, 0.35 MB re-encoded — so
       running well ahead of the viewer costs almost nothing and buys immunity
       to a slow machine. 90 s ≈ 2 MB. */
    var aheadSec = Math.max(segLen * 2, +opts.aheadSec || 90);
    var canRun = (typeof opts.canRun === 'function') ? opts.canRun : function () { return true; };

    var cb = {
        first: opts.onFirstSound || function () {},
        prog: opts.onProgress || function () {},
        err: opts.onError || function () {},
        lane: opts.onLane || function () {},
        wait: opts.onWaiting || function () {}
    };

    var stopped = false, ms = null, sb = null, url = '', audio = null;
    var sync = null, silencer = null, lane = '', mime = '';
    var readySec = 0, segCount = 0, firstFired = false;
    var pendingSeek = null, atEof = false, waiting = false, waitedMs = 0, waitedFrom = 0;
    var lastMoveAt = _visibleNow(), deadTimer = null;
    var soundResolve, soundReject;
    /* Resolves the moment the chosen track is actually audible — that, not the
       end of the pump, is what the caller waits on to take its spinner down. */
    var sounding = new Promise(function (res, rej) { soundResolve = res; soundReject = rej; });
    sounding.catch(function () {});

    /* The element is created and armed HERE, synchronously inside the click
       that asked for the track. The first segment lands seconds later, and by
       then Safari and Chromium-on-Android have dropped the activation — an
       element that has never played inside a gesture is refused for good.
       (public/js/audio-gate.js, measured on /english-level-test.) */
    audio = document.createElement('audio');
    audio.preload = 'auto';
    audio.playsInline = true;
    try { audio.setAttribute('playsinline', ''); } catch (_) {}
    audio.setAttribute('data-audio-track-sidecar', '1');
    audio.style.cssText = 'position:absolute;width:0;height:0;opacity:0;pointer-events:none';
    try {
        if (window.__audioGate) { window.__audioGate.arm(audio); window.__audioGate.unlockNow(audio); }
    } catch (_) {}
    try { (opts.mount || document.body).appendChild(audio); } catch (_) {}

    function codecLane() {
        var c = String(opts.codec || '').toLowerCase().replace(/^e?-?ac-?3$/, 'ac3');
        var m = COPY_MIME[c];
        if (!m) return { lane: 'aac', mime: AAC_MIME };
        try { var C = _mseCtor(); if (!C || !C.isTypeSupported(m)) return { lane: 'aac', mime: AAC_MIME }; }
        catch (_) { return { lane: 'aac', mime: AAC_MIME }; }
        return { lane: 'copy', mime: m, codec: c };
    }

    function segArgs(dur) {
        return ['-t', String(dur), '-vn', '-sn', '-map', '0:a:' + relIndex]
            .concat(lane === 'copy' ? COPY_ARGS : AAC_ARGS)
            .concat(FRAG);
    }

    function bufferedHas(t) {
        try {
            if (!sb) return false;
            for (var i = 0; i < sb.buffered.length; i++) {
                if (t >= sb.buffered.start(i) - 0.05 && t < sb.buffered.end(i) - 0.25) return true;
            }
        } catch (_) {}
        return false;
    }
    function bufferedEndFrom(t) {
        try {
            if (!sb) return t;
            for (var i = 0; i < sb.buffered.length; i++) {
                if (t >= sb.buffered.start(i) - 0.05 && t < sb.buffered.end(i)) return sb.buffered.end(i);
            }
        } catch (_) {}
        return t;
    }

    function append(buf, offset) {
        return new Promise(function (res, rej) {
            if (!sb || stopped) return res();
            function ok() { cleanup(); res(); }
            /* An 'error' Event carries no .message; rejecting with it reaches
               telemetry as an empty reason. Name the state instead — the same
               correction progressive-decode.js already carries. */
            function bad(e) {
                cleanup();
                rej(new Error((e && e.message) || ('append_failed (mime=' + mime + ', ms=' + (ms && ms.readyState) + ')')));
            }
            function cleanup() { sb.removeEventListener('updateend', ok); sb.removeEventListener('error', bad); }
            sb.addEventListener('updateend', ok);
            sb.addEventListener('error', bad);
            try {
                sb.timestampOffset = offset;
                sb.appendBuffer(buf);
            } catch (e) { cleanup(); rej(e); }
        });
    }

    function isQuota(e) {
        return !!e && (e.name === 'QuotaExceededError' || /quota|SourceBuffer is full/i.test(e.message || ''));
    }
    /* A whole 24-minute track is ~20-33 MB, well inside the SourceBuffer's
       budget, so this only ever fires on a feature-length film. Drop what is
       behind the viewer, exactly like the video pump does. */
    function evictBehind() {
        return new Promise(function (res) {
            if (!sb || stopped || sb.updating) return res(false);
            var keepFrom = Math.max(0, (video ? video.currentTime : 0) - 30);
            var first = 0;
            try { first = sb.buffered.length ? sb.buffered.start(0) : 0; } catch (_) { return res(false); }
            if (!(keepFrom > first + 1)) return res(false);
            function fin() { sb.removeEventListener('updateend', fin); res(true); }
            sb.addEventListener('updateend', fin);
            try { sb.remove(first, keepFrom); }
            catch (_) { sb.removeEventListener('updateend', fin); res(false); }
        });
    }

    var ready = new Promise(function (resolve, reject) {
        if (!isSupported()) return reject(new Error('sidecar_unsupported'));
        if (!video) return reject(new Error('sidecar_no_video'));
        var pick = codecLane();
        lane = pick.lane; mime = pick.mime;
        var C = _mseCtor();
        if (!C) return reject(new Error('sidecar_unsupported'));
        ms = new C();
        /* ManagedMediaSource refuses to attach to an element that can hand the
           stream to an AirPlay receiver — the engine cannot manage a buffer it
           does not own. Harmless on plain MediaSource. */
        try { audio.disableRemotePlayback = true; } catch (_) {}
        url = URL.createObjectURL(ms);
        try { if (window.__skipBlobAutoTrack) window.__skipBlobAutoTrack(ms); } catch (_) {}
        ms.addEventListener('sourceopen', function () {
            try {
                sb = ms.addSourceBuffer(mime);
            } catch (e) {
                /* The string said yes and the buffer said no. Fall to the lane
                   every engine takes rather than failing the switch. */
                if (lane === 'copy') {
                    lane = 'aac'; mime = AAC_MIME;
                    try { sb = ms.addSourceBuffer(mime); } catch (e2) { return reject(e2); }
                } else { return reject(e); }
            }
            /* ABSOLUTE timeline: each segment is produced by `-ss pos`, so its
               own timestamps start at ~0 and timestampOffset puts it back where
               it belongs. 'sequence' (what the video pump uses) would stack
               every segment at the head and make a seek unrepresentable. */
            sb.mode = 'segments';
            if (duration) { try { ms.duration = duration; } catch (_) {} }
            resolve();
        }, { once: true });
        audio.src = url;
        _visibleDeadline(function () { if (!sb) reject(new Error('sourceopen_timeout')); }, 20000);
    });

    function onSeeking() {
        if (stopped || !video) return;
        var t = video.currentTime;
        if (bufferedHas(t)) return;
        pendingSeek = t;
    }
    if (video) video.addEventListener('seeking', onSeeking);

    /* ── The watchdog on the thing the viewer is actually waiting for ─────
       Everything below can fail without throwing: a SourceBuffer that takes the
       bytes and never reaches readyState 2, an engine that answers and produces
       husks, a MediaSource variant this browser exposes but does not feed. Each
       of those leaves whenSounding() pending forever, i.e. the exact spinner
       this whole change exists to remove — just one layer down.
       So: judge by SILENCE, not by elapsed time. Time spent QUEUEING behind
       another job on the shared engine is not our silence and is excluded
       (`waiting`), because on a large file that queue is legitimately tens of
       seconds. Firing hands the caller back to the whole-file path, which is
       slow but known to work. */
    var DEAD_AFTER_MS = 45000;
    deadTimer = setInterval(function () {
        if (stopped || firstFired) { clearInterval(deadTimer); deadTimer = null; return; }
        if (waiting) { lastMoveAt = _visibleNow(); return; }
        if (_visibleNow() - lastMoveAt < DEAD_AFTER_MS) return;
        clearInterval(deadTimer); deadTimer = null;
        ae('audio_sidecar_no_sound', { idx: relIndex, lane: lane, segments: segCount });
        soundReject(new Error('sidecar_no_sound'));
    }, 2000);

    var loop = ready.then(async function () {
        if (!window.FFmpegProcessor.isReady()) await window.FFmpegProcessor.init();
        var pos = Math.max(0, video ? video.currentTime : 0);
        var laneProven = false;
        while (!stopped) {
            if (pendingSeek != null) { pos = Math.max(0, pendingSeek); pendingSeek = null; atEof = false; }
            if (duration && pos >= duration - 0.25) {
                /* Nothing left to produce ahead of here, but a seek backwards
                   can still ask for more, so the pump idles instead of exiting.
                   endOfStream() is deliberately never called: the sidecar
                   starts at the viewer's position, so the head of the timeline
                   is legitimately empty and "no more data is coming" would be a
                   lie the moment they scrub back. */
                atEof = true;
                /* Switching in the last seconds of the film would otherwise
                   produce nothing at all, the watchdog would call it dead and
                   the caller would pay a whole-file remux for a track nobody
                   gets to hear. Back up one segment so there is always
                   something to sound. */
                if (!firstFired && duration > 1) { pos = Math.max(0, duration - segLen); atEof = false; continue; }
                await sleep(1000);
                continue;
            }
            /* The viewer comes first. `pos` is only where we INTENDED to
               produce next; between deciding that and getting the engine the
               film keeps playing, and on the progressive tier that gap is a
               whole video segment. Producing sound the viewer has already
               walked past is how the first switch came out 3.6 s behind and
               silent (measured on the segmented tier before this line existed).
               So: if there is nothing to hear where they ARE, start there. */
            var head = video ? video.currentTime : 0;
            if (!bufferedHas(head) && pos < head - 0.5) pos = head;
            /* Already produced? Skip forward instead of re-extracting — this is
               what makes seeking back into a watched stretch free. */
            if (bufferedHas(pos)) { pos = bufferedEndFrom(pos); continue; }
            /* Stay ahead of the viewer, not ahead of the file. */
            if (pos - head > aheadSec) { await sleep(500); continue; }
            /* The shared FFmpeg worker is busy with something else — the
               tool's own background probe / subtitle pass on a big file is a
               full read and takes tens of seconds. Waiting is right (two
               exec()s at once corrupt each other), saying nothing is not: an
               unchanging "switching…" is exactly what the report called
               infinite loading. */
            if (!canRun()) {
                if (!waiting) { waiting = true; waitedFrom = (window.performance || Date).now(); cb.wait(true); }
                await sleep(120);
                continue;
            }
            if (waiting) { waiting = false; waitedMs += Math.round((window.performance || Date).now() - waitedFrom); cb.wait(false); }

            var want = duration ? Math.min(segLen, duration - pos) : segLen;
            var t0 = (window.performance || Date).now();
            var out;
            try {
                out = await window.FFmpegProcessor.processStreaming(
                    file, segArgs(want), 'atrack.mp4', undefined, ['-ss', String(pos)]);
            } catch (e) {
                /* The muxer refused to copy this codec through. The buffer was
                   built promising that codec, so the whole source has to be
                   rebuilt on AAC — switching `lane` alone would append AAC into
                   a FLAC SourceBuffer. Nothing is audible yet, so nothing is
                   interrupted. */
                if (!laneProven && lane === 'copy') {
                    ae('audio_sidecar_copy_unmuxable', { idx: relIndex, codec: String(opts.codec || '') });
                    if (await rebuildAsAac()) continue;
                    throw e;
                }
                if (segCount === 0) throw e;
                ae('audio_sidecar_engine_error', { idx: relIndex, msg: String((e && e.message) || e).slice(0, 120) });
                break;
            }
            if (stopped) break;
            var bytes = new Uint8Array(await out.blob.arrayBuffer());
            try { URL.revokeObjectURL(out.url); } catch (_) {}
            if (stopped) break;
            lastMoveAt = _visibleNow();
            if (bytes.length < EMPTY_SEGMENT_BYTES) { atEof = true; pos += want; continue; }

            var placedAt = pos;
            try {
                await append(bytes, placedAt);
            } catch (e) {
                if (isQuota(e)) {
                    var freed = await evictBehind();
                    if (freed) { try { await append(bytes, placedAt); } catch (e3) { if (segCount === 0) throw e3; break; } }
                    else { await sleep(1000); continue; }
                } else if (!laneProven && lane === 'copy') {
                    /* The buffer took the init segment and refused the media —
                       the copy lane is a lie on this engine. Rebuild the whole
                       source on AAC; nothing has been heard yet. */
                    ae('audio_sidecar_copy_refused', { idx: relIndex, codec: String(opts.codec || '') });
                    if (!(await rebuildAsAac())) throw e;
                    continue;
                } else if (segCount === 0) { throw e; }
                else { ae('audio_sidecar_append_error', { idx: relIndex, msg: String(e.message || '').slice(0, 120) }); break; }
            }
            laneProven = true;
            segCount++;
            readySec = Math.max(readySec, placedAt + want);
            pos = placedAt + want;
            if (!firstFired) {
                firstFired = true;
                var ms1 = (window.performance || Date).now() - t0;
                cb.lane(lane);
                /* Nothing was audible until now: the element has a source but
                   no data at the viewer's position. Line it up and start. */
                /* `waitedMs` is queueing on the shared engine, not our cost —
                   without it the two are indistinguishable in telemetry and a
                   fast switch behind a slow queue reads as a slow switch. */
                if (deadTimer) { clearInterval(deadTimer); deadTimer = null; }
                var info = { lane: lane, ms: Math.round(ms1), at: placedAt, waitedMs: waitedMs };
                if (!(await startPlayback())) { soundReject(new Error('sidecar_not_audible')); return; }
                info.ms = Math.round((window.performance || Date).now() - t0);
                soundResolve(info);
                cb.first(info);
            }
            cb.prog({ readySec: readySec, segments: segCount, lane: lane });
            /* Yield: let the element decode and the page paint. The first pause
               is longer so playback really begins before we compete again. */
            await sleep(segCount === 1 ? 250 : 20);
        }
    }).catch(function (e) {
        if (!stopped) { soundReject(e); cb.err(e); }
    });

    function sleep(ms2) { return new Promise(function (r) { setTimeout(r, ms2); }); }

    /* Swap the MediaSource for one on the AAC lane. Only reachable before the
       first successful append, so no sound is interrupted. */
    async function rebuildAsAac() {
        lane = 'aac'; mime = AAC_MIME;
        try { if (url) URL.revokeObjectURL(url); } catch (_) {}
        sb = null;
        var C2 = _mseCtor();
        if (!C2) return false;
        ms = new C2();
        try { audio.disableRemotePlayback = true; } catch (_) {}
        url = URL.createObjectURL(ms);
        try { if (window.__skipBlobAutoTrack) window.__skipBlobAutoTrack(ms); } catch (_) {}
        var opened = new Promise(function (res) {
            ms.addEventListener('sourceopen', function () {
                try { sb = ms.addSourceBuffer(mime); sb.mode = 'segments'; if (duration) ms.duration = duration; res(true); }
                catch (_) { res(false); }
            }, { once: true });
            _visibleDeadline(function () { res(!!sb); }, 15000);
        });
        audio.src = url;
        return await opened;
    }

    /* ── "the buffer took it" is not "the viewer can hear it" ──────────────
       A SourceBuffer accepting bytes proves the container was parsed, nothing
       more. An engine can accept every append and never decode: the element
       stays at readyState 0-1 and its clock never moves. Reporting success on
       the append alone would silence the video (we do that ourselves) and leave
       the film with NO sound at all — strictly worse than the whole-file wait
       this module exists to remove, and invisible to any check that asks the
       buffer instead of the element.

       So prove it the way this codebase proves everything else about media:
       drive it and watch the clock (canPlayNatively / canPlayAudibly do the
       same). The sidecar plays MUTED during the proof, so the file's own track
       keeps playing meanwhile — the viewer hears one language throughout, never
       two, and never a gap. Only once the sidecar is really decoding do we
       silence the video and unmute. */
    var PROVE_MS = 8000;
    function proveAudible() {
        return new Promise(function (resolve) {
            var done = false, t0 = (window.performance || Date).now();
            var startedAt = -1;
            function finish(ok, why) { if (done) return; done = true; clearInterval(iv); resolve({ ok: ok, why: why }); }
            var iv = setInterval(function () {
                if (stopped) return finish(false, 'stopped');
                if (audio.error) return finish(false, 'element_error');
                if (audio.readyState >= 2) {
                    /* Decoded data is at the playhead. If the film is paused
                       that is all we can ask for; if it is playing, the clock
                       has to actually move. */
                    if (video.paused) return finish(true, 'ready_paused');
                    if (startedAt < 0) { startedAt = audio.currentTime; return; }
                    if (audio.currentTime > startedAt + 0.01) return finish(true, 'clock_moving');
                }
                if ((window.performance || Date).now() - t0 > PROVE_MS) {
                    finish(false, 'rs=' + audio.readyState + ' t=' + audio.currentTime.toFixed(2));
                }
            }, 120);
        });
    }

    async function startPlayback() {
        if (!video) return false;
        /* Muted while we prove it — see above. VideoAudioSync would mirror the
           video's volume onto the element, so it is attached only after. */
        audio.muted = true;
        try { audio.currentTime = video.currentTime; } catch (_) {}
        if (!video.paused) {
            var p0 = audio.play();
            if (p0 && p0.catch) p0.catch(function (err) { ae('audio_sidecar_play_refused', { name: (err && err.name) || '' }); });
        }
        var proof = await proveAudible();
        if (stopped) return false;
        if (!proof.ok) {
            ae('audio_sidecar_not_audible', { idx: relIndex, lane: lane, why: proof.why });
            try { audio.pause(); } catch (_) {}
            return false;
        }
        silencer = silenceVideo(video);
        if (!silencer) {
            /* No WebAudio silencer means the file's own track would play under
               the chosen one. Two voices at once is worse than the old wait, so
               the caller must be told rather than shipped a mess. */
            try { audio.pause(); } catch (_) {}
            return false;
        }
        audio.muted = video.muted;
        audio.volume = video.volume;
        sync = window.VideoAudioSync.attach(video, audio, {
            softThreshold: 0.04, hardThreshold: 0.25, intervalMs: 400
        });
        return true;
    }

    function stop() {
        if (stopped) return;
        stopped = true;
        /* A caller awaiting first sound must not be left hanging when the
           switch is cancelled (new file, another track picked, page reset). */
        if (!firstFired) soundReject(new Error('sidecar_stopped'));
        if (deadTimer) { clearInterval(deadTimer); deadTimer = null; }
        if (video) { try { video.removeEventListener('seeking', onSeeking); } catch (_) {} }
        if (sync) { try { sync.detach(); } catch (_) {} sync = null; }
        if (silencer) { try { silencer.release(); } catch (_) {} silencer = null; }
        try { audio.pause(); } catch (_) {}
        try { audio.removeAttribute('src'); audio.load(); } catch (_) {}
        try { if (audio.parentNode) audio.parentNode.removeChild(audio); } catch (_) {}
        try { if (url) URL.revokeObjectURL(url); } catch (_) {}
        sb = null; ms = null;
    }

    return {
        element: function () { return audio; },
        lane: function () { return lane; },
        relIndex: function () { return relIndex; },
        readySeconds: function () { return readySec; },
        segments: function () { return segCount; },
        /* Resolves when the chosen track is audible, rejects when it cannot be.
           This — not the end of the pump — is what a caller waits on. */
        whenSounding: function () { return sounding; },
        whenDone: function () { return loop; },
        stop: stop
    };
}

window.AudioTrackSidecar = { attach: attach, isSupported: isSupported, __isStub: false };
})();
